import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TablePagination
} from '@material-ui/core';
import { TABLE_HEADER_STYLE, TABLE_CELL_STYLE, TABLE_PAGINATION_STYLE, TABLE_SORT_LABEL_STYLE } from 'utils/tableStyles';
import { GLOBAL_TABLE_PAGE_SIZE } from 'global_config/constant-global';
import { logAuditTrailEvent } from 'services/system_setup/auditTrail';

// Sorting utility functions
function parseDateValue(value) {
    if (!value) return null;
    if (value instanceof Date) {
        const time = value.getTime();
        return Number.isNaN(time) ? null : time;
    }
    if (typeof value !== 'string') return null;

    const trimmedValue = value.trim();
    const mmddyyyyMatch = trimmedValue.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (mmddyyyyMatch) {
        const month = Number(mmddyyyyMatch[1]);
        const day = Number(mmddyyyyMatch[2]);
        const year = Number(mmddyyyyMatch[3]);
        const parsedTime = new Date(year, month - 1, day).getTime();
        return Number.isNaN(parsedTime) ? null : parsedTime;
    }

    const fallbackParsedTime = Date.parse(trimmedValue);
    return Number.isNaN(fallbackParsedTime) ? null : fallbackParsedTime;
}

function getComparableValue(record, orderBy) {
    const value = record && record[orderBy];
    const isDateField = typeof orderBy === 'string' && orderBy.toLowerCase().includes('date');

    if (isDateField) {
        const parsedDate = parseDateValue(value);
        if (parsedDate !== null) return parsedDate;
    }

    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
            return Number(trimmedValue);
        }
        return trimmedValue.toUpperCase();
    }

    if (value === null || value === undefined) return '';
    return String(value).toUpperCase();
}

function descendingComparator(a, b, orderBy) {
    const valueA = getComparableValue(a, orderBy);
    const valueB = getComparableValue(b, orderBy);

    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const columns = [
    { title: 'D.R. NO.', dataIndex: 'dr_no', key: 'dr_no', sortable: true, width: '15%' },
    { title: 'D.R DATE', dataIndex: 'formated_dr_date', key: 'dr_date', sortable: true, width: '15%' },
    { title: 'CUSTOMER', dataIndex: 'customer', key: 'customer', width: '45%' },
    { title: 'STATUS', dataIndex: 'status_name', key: 'status_name', width: '25%' },
];

function SearchTable(props) {
    const history = useHistory();
    const { data = [] } = props;

    const [orderBy, setOrderBy] = useState('formated_dr_date');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(0);

    // Sync page when pageSize changes from SearchPager (props)
    useEffect(() => { setPage(0); }, [props.pageSize]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

                const comparator = useMemo(() => getComparator(order, orderBy), [order, orderBy]);
            const sortedData = useMemo(() => {
                if (!data || !Array.isArray(data)) return [];
                return stableSort(data, comparator);
            }, [data, comparator]);

        const pagedData = useMemo(() => {
            const ps = props.pageSize || GLOBAL_TABLE_PAGE_SIZE;
            const start = page * ps;
            return sortedData.slice(start, start + ps);
        }, [sortedData, page, props.pageSize]);

        const handlePageChange = (event, newPage) => {
            // console.log('handlePageChange =>', newPage);
            setPage(newPage);
        };

    // rows-per-page changes are handled by parent SearchPager via props.handleChangePageSize
    const onRowsPerPageChange = (e) => {
        const newSize = parseInt(e.target.value, 10);
        if (props.handleChangePageSize) props.handleChangePageSize(newSize);
    };

    const handleOpenRecord = (record) => {
        void logAuditTrailEvent({
            moduleCode: 'dr',
            subModuleCode: 'search',
            action: 'VIEW',
            description: `View delivery record ${record && record.dr_no ? record.dr_no : ''}`.trim(),
            payload: {
                dr_id: record && record.dr_id,
                dr_no: record && record.dr_no,
            },
            isSuccess: 1,
        });

        history.push({
            pathname: '/delivery/details_update',
            state: { record },
        });
    };

            return (
                <div>
                <TableContainer component={Paper} style={{ marginTop: 8 }}>
                    <MuiTable stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell key={col.key || col.dataIndex} style={{ ...TABLE_HEADER_STYLE, width: col.width }}>
                                        {col.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === col.dataIndex}
                                                    // @ts-ignore ensure correct literal type for direction
                                                    direction={orderBy === col.dataIndex ? order : 'asc'}
                                                onClick={() => handleRequestSort(col.dataIndex)}
                                                style={TABLE_SORT_LABEL_STYLE}
                                            >
                                                {col.title}
                                            </TableSortLabel>
                                        ) : (
                                            col.title
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody style={{ textTransform: 'uppercase' }}>
                            {pagedData.map((record, idx) => (
                                <TableRow
                                    hover
                                    key={`${record.dr_no}-${idx}`}
                                    onDoubleClick={() => handleOpenRecord(record)}
                                >
                                    {columns.map(col => (
                                        <TableCell key={col.key || col.dataIndex} style={TABLE_CELL_STYLE}>
                                            {record[col.dataIndex]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {pagedData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" style={TABLE_CELL_STYLE}>
                                        No records found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </MuiTable>
                </TableContainer>
                    {/* @ts-ignore v4 props; ignored for checkJs compatibility */}
                    <TablePagination
                        component="div"
                        // rowsPerPageOptions={[5, 10, 20, 50]}
                        count={data ? data.length : 0}
                        rowsPerPage={props.pageSize}
                        page={page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={onRowsPerPageChange}
                        rowsPerPageOptions={[]}
                        style={TABLE_PAGINATION_STYLE}
                        // labelRowsPerPage=""
                    />
            </div>
        );
}

export default SearchTable;
